<template>
    <common-content-spacer height="medium" class="referral-show-details">
        <common-content-group
            :isCompact="true"
            :label="$t(`common.referral_details.${isReferred ? 'received_from' : 'sent_to'}`)"
        >
            <common-content-spacer height="medium">
                <user-details
                    v-if="isUserCard() && referral.fromUser"
                    :user="physician"
                    :bottomText="physicianSpeciality"
                    :showEmail="false"
                    :showPhone="false"
                ></user-details>
                <patient-details v-else-if="!referral.fromUser" :user="patient"></patient-details>

                <practice-card v-else :practice="isReferred ? fromPractice : toPractice" :isStandalone="false"></practice-card>

                <subscribe-to-premium-tooltip-wrapper :show="isToUserSubscriptionNetwork">
                    <div
                        v-if="userId && !isSelfReferred && referral.fromUser"
                        :class="{ 'referral-show-details__disabled': isToUserSubscriptionNetwork }"
                        @click="!isToUserSubscriptionNetwork && openChatModal()"
                    >
                        <span v-if="shouldShowPhysicianSendMessage" :class="sendMessageClasses">
                            {{ $t('common.referral_details.send_message', { name: messageRecipient }) }}
                        </span>
                        <span v-if="shouldShowAncillarySendMessage" :class="sendMessageClasses">
                            {{ $t('common.referral_details.send_message_ancillary', { name: messageRecipient }) }}
                        </span>
                    </div>
                </subscribe-to-premium-tooltip-wrapper>
                <subscribe-to-premium-tooltip-wrapper :show="isToUserSubscriptionNetwork">
                    <div
                        v-if="referral?.patient?.id"
                        :class="{ 'referral-show-details__disabled': isToUserSubscriptionNetwork }"
                        @click="!isToUserSubscriptionNetwork && openChatModal(true)"
                    >
                        <span v-if="isReferred" :class="sendMessageClasses">
                            {{ $t('common.referral_details.send_patient_message', { name: messagePatient }) }}
                        </span>
                    </div>
                </subscribe-to-premium-tooltip-wrapper>

                <referral-chat-modal
                    :isOpen="isChatModalOpen"
                    :referral="referral"
                    :isChatWithPatient="isChatWithPatient"
                    :isBothPracticesAncillary="isBothPracticesAncillary"
                    @close="closeChatModal"
                ></referral-chat-modal>
                <h6 v-if="referral.note" :class="referral.shouldShowPatientInfo ? '' : 'text-blured'">
                    {{ $t('common.referral_details.referral_note') }}
                </h6>
                <common-quote
                    v-if="referral.note"
                    :class="referral.shouldShowPatientInfo ? '' : 'text-blured'"
                    :content="referral.note"
                    :author="referringPhysicianName"
                ></common-quote>
            </common-content-spacer>
        </common-content-group>

        <p
            v-if="!showPrimaryCarePhysicianDetails && !isBothPracticesAncillary && referral.primaryCarePhysician?.id"
            class="referral-show-details__show-primary"
            @click="togglePrimaryCarePhysicianDetails"
        >
            {{ $t('common.referral_details.show_pcp') }}
        </p>
        <common-content-group
            v-if="showPrimaryCarePhysicianDetails && !isBothPracticesAncillary"
            :isCompact="true"
            :label="$t('common.referral_details.pcp')"
        >
            <user-details
                :user="primaryCarePhysician"
                :bottomText="$t('common.practice_type.physician')"
                :showEmail="true"
                :showPhone="true"
            ></user-details>
        </common-content-group>
    </common-content-spacer>
</template>

<script>
    import { composeDisplayName } from 'components/common/CommonUserName'
    import UserDetails from 'components/page-elements/referrals/details/UserDetailsCard'
    import ReferralChatModal from 'components/page-elements/referrals/messages/ReferralChatModal'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import MessagesAPI from 'api/MessagesAPI'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import PatientDetails from 'components/page-elements/referrals/details/PatientDetailsCard'
    import SubscribeToPremiumTooltipWrapper from '../SubscribeToPremiumTooltipWrapper.vue'
    export default {
        components: {
            UserDetails,
            ReferralChatModal,
            PracticeCard,
            PatientDetails,
            SubscribeToPremiumTooltipWrapper,
        },

        props: {
            referral: { type: Object, required: true },
            type: { type: String, required: true },
        },

        data() {
            return {
                showPrimaryCarePhysicianDetails: false,
                showPatientReviewModal: false,
                highlightedRatingKey: 'overallSatisfaction',
                isChatModalOpen: false,
                referralMessage: null,
                userId: null,
                PracticeTypeEnum: PracticeTypeEnum,
                isChatWithPatient: false,
            }
        },

        computed: {
            shouldShowPhysicianSendMessage() {
                return (
                    (this.referral?.practice && this.referral.practice.practiceType === PracticeTypeEnum.PHYSICIAN) ||
                    this.isReferred
                )
            },
            sendMessageClasses() {
                return [
                    'referral-show-details__send-text',
                    {
                        'referral-show-details__send-text--disabled': this.isToUserSubscriptionNetwork,
                    },
                ]
            },
            shouldShowAncillarySendMessage() {
                return (
                    this.referral?.practice &&
                    this.referral.practice.practiceType === PracticeTypeEnum.ANCILLARY &&
                    !this.isReferred
                )
            },
            isToUserSubscriptionNetwork() {
                return this.$auth.isPracticeFreePlan()
            },
            physicianSpeciality() {
                return this.referral.fromUser && this.referral?.fromUser.practiceType !== PracticeTypeEnum.OTHER
                    ? this.$auth.getUser().id === this.referral.fromUser.id
                        ? this.referral.toUser.specialities[0].speciality.name
                        : this.referral.fromUser.specialities[0].speciality.name
                    : ''
            },
            getMessage() {
                if (this.referralMessage && this.referralMessage.length > 0) {
                    let lastMessage = { ...this.referralMessage[this.referralMessage.length - 1] } // Pravljenje kopije objekta
                    let div = document.createElement('div')
                    div.innerHTML = lastMessage.body
                    let textContent = div.textContent || div.innerText || ''
                    if (textContent.length > 147) {
                        let truncatedText = textContent.substring(0, 147) + '...'
                        div.textContent = truncatedText
                        lastMessage.body = div.innerHTML
                    }

                    return lastMessage
                }
                return {}
            },

            isSelfReferred() {
                return (
                    this.referral?.toUser?.id === this.referral?.fromUser?.id ||
                    this.referral?.practice?.id === this.referral?.fromPractice?.id
                )
            },

            isBothPracticesAncillary() {
                return (
                    this.referral?.practice?.practiceType === PracticeTypeEnum.ANCILLARY &&
                    this.referral?.fromPractice?.practiceType === PracticeTypeEnum.ANCILLARY
                )
            },
            messageRecipient() {
                if (!this.referral.fromUser) {
                    return ''
                }

                if (this.userId !== this.referral.fromUser.id) {
                    return this.referral.fromUser?.firstName + ' ' + this.referral.fromUser?.lastName
                }

                if (this.referral.toUser) {
                    return this.referral.toUser?.firstName + ' ' + this.referral.toUser?.lastName
                }

                return this.referral.practice.name
            },
            messagePatient() {
                if (!this.referral) return ''
                return this.referral.patient?.firstName + ' ' + this.referral.patient?.lastName
            },
            isReferred() {
                return this.type === ReferralPathTypeEnum.RECEIVED
            },

            physician() {
                return this.referral[this.isReferred ? 'fromUser' : 'toUser'] || {}
            },

            patient() {
                return this.referral.patient || {}
            },

            fromPractice() {
                return this.referral.fromPractice || {}
            },

            toPractice() {
                return this.referral?.practice || {}
            },

            primaryCarePhysician() {
                return this.referral.primaryCarePhysician || {}
            },

            physicianName() {
                const name = composeDisplayName(this.physician)
                return this.physician?.designation ? `${name}, ${this.physician.designation}` : name
            },

            referringPhysicianName() {
                const user = this.referral.fromUser
                if (!user) return ''
                const name = composeDisplayName(user)
                return user.designation ? `${name}, ${user.designation}` : name
            },

            patientRating() {
                if (this.referral.patientReview) {
                    let keys = [
                        'officeEnvironment',
                        'staffFriendliness',
                        'waitTime',
                        'levelOfTrust',
                        'providerExplanation',
                        'providerListening',
                        'timeSpent',
                    ]
                    let sum = keys.reduce((sum, key) => {
                        sum = sum + Number(this.referral.patientReview[key])
                        return sum
                    }, 0)

                    return Number((sum / keys.length).toFixed(0))
                }

                return null
            },
            ratingsForModal() {
                if (this.referral.patientReview) {
                    let rev = this.referral.patientReview
                    let ratingsWithOverall = [
                        {
                            key: this.highlightedRatingKey,
                            text: this.$t('pages.referrals.patient_review.ratings.overrall.text'),
                            grade: this.patientRating,
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.office.text'),
                            key: 'officeEnvironment',
                            grade: Number(rev['officeEnvironment']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.stuff.text'),
                            key: 'staffFriendliness',
                            grade: Number(rev['staffFriendliness']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.wait_time.text'),
                            key: 'waitTime',
                            grade: Number(rev['waitTime']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.trust.text'),
                            key: 'levelOfTrust',
                            grade: Number(rev['levelOfTrust']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.explanation.text'),
                            key: 'providerExplanation',
                            grade: Number(rev['providerExplanation']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.listening.text'),
                            key: 'providerListening',
                            grade: Number(rev['providerListening']),
                        },
                        {
                            text: this.$t('pages.referrals.patient_review.ratings.time_spent.text'),
                            key: 'timeSpent',
                            grade: Number(rev['timeSpent']),
                        },
                    ]

                    return ratingsWithOverall
                }

                return null
            },
            summaryTextForPatientReviewModal() {
                let patient = this.referral.patient
                let physician = this.referral.toUser

                return `${patient.firstName} ${patient.lastName} rated Dr. ${physician.firstName} ${
                    physician.lastName
                } with ${Number(this.patientRating).toFixed(0)}`
            },
            shouldDisplayPatientReview() {
                return this.referral && this.referral.patientReview && this.$route.params.type == 'made'
            },
        },
        methods: {
            isUserCard() {
                if (this.isReferred) {
                    return (
                        this.referral?.fromPractice?.practiceType === PracticeTypeEnum.PHYSICIAN ||
                        (this.referral?.fromUser && this.referral?.fromUser.practiceType === PracticeTypeEnum.OTHER)
                    )
                } else {
                    return this.referral?.practice?.practiceType === PracticeTypeEnum.PHYSICIAN
                }
            },
            togglePrimaryCarePhysicianDetails() {
                this.showPrimaryCarePhysicianDetails = !this.showPrimaryCarePhysicianDetails
            },
            closeChatModal() {
                this.isChatWithPatient = false
                this.$router.push({ path: this.$route.path, query: {} })
                this.isChatModalOpen = false
                this.getMessages()
            },
            openChatModal(isChatWithPatient) {
                this.isChatWithPatient = isChatWithPatient
                if (this.$route.query.chatType === 'patients') {
                    this.isChatWithPatient = true
                }
                this.isChatModalOpen = true
            },
            viewPatientReviewDetailsClicked() {
                this.showPatientReviewModal = true
            },
            viewPatientReviewDetailsModalClosed() {
                this.showPatientReviewModal = false
            },
            getMessages() {
                if (!this.userId || !this.referral.id) {
                    return
                }

                const query = {
                    objectId: this.referral.id,
                    userId:
                        (this.practice && this.practice.practiceType === PracticeTypeEnum.PHYSICIAN) ||
                        this.$auth.isUserPracticeOther()
                            ? this.userId
                            : 0,
                    objectType: 'referral',
                    limit: 1,
                    order: 'desc',
                }

                if (this.practice) {
                    query.practiceId = this.practice.id
                } else {
                    query.practiceId = 0
                }

                return MessagesAPI.getMessages(query).then((resp) => {
                    if (resp.length) {
                        this.referralMessage = resp
                    }
                    return resp
                })
            },
        },
        watch: {
            referral() {
                if (this.$route.query.openChat) this.openChatModal()
                this.getMessages()
            },
        },
        mounted() {
            this.practice = !this.$auth.isUserPracticeOther() ? this.$auth.getPractice() : undefined
            this.userId = this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id
        },
    }
</script>
